import { initializeApp } from 'firebase/app'
import {
  getToken,
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check'
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  setPersistence,
} from 'firebase/auth'
import { getStorage } from 'firebase/storage'

/**
 * Die Werte hier sind keine Geheimnisse. Ein Firebase-Web-Schlüssel benennt
 * nur das Projekt; was ein Konto darf, entscheiden die Regeln auf der
 * Serverseite — hier die Prüfung gegen `core_role_assignment`.
 */
const konfiguration = {
  apiKey: 'AIzaSyCv49iyDDD9uqUnk5CSwHjkDj08aUe7c7Q',
  authDomain: 'childs-play-fcc3a.firebaseapp.com',
  projectId: 'childs-play-fcc3a',
  storageBucket: 'childs-play-fcc3a.appspot.com',
  messagingSenderId: '906763447484',
  appId: '1:906763447484:web:7d02e6de41085aa2d6b4b9',
}

/**
 * Der Site Key für App Check. Öffentlich wie der API-Schlüssel oben.
 *
 * **Es ist ein reCAPTCHA-Enterprise-Schlüssel** (Typ SCORE), kein klassischer
 * v3. Beide beginnen mit `6L`, sind aber nicht austauschbar: Enterprise hat
 * kein Secret und wird über `ReCaptchaEnterpriseProvider` samt dem
 * Enterprise-Feld in App Check angesprochen. Ein Enterprise-Schlüssel im
 * v3-Feld quittiert der Austausch mit 400 „Invalid reCAPTCHA configuration".
 *
 * **Warum das hier stehen muss:** App Check ist im Projekt für
 * `firebasestorage.googleapis.com` erzwungen. Ohne gültiges Token antwortet
 * Storage auf **jeden** Bildabruf mit 401 „Firebase App Check token is
 * invalid" — auch bei Bildern, die einwandfrei im Bucket liegen. Die
 * iOS-App schickt ihr Token mit; dieses Dashboard braucht ein eigenes.
 */
const RECAPTCHA_SITE_KEY = '6LdzN4otAAAAAMHgTtGdrIAcL2bOXC1qR5zzEGPq'

export const firebaseApp = initializeApp(konfiguration)

/**
 * App Check vor allem anderen: Ein Aufruf, der vor der Initialisierung
 * losgeht, trägt kein Token und wird abgewiesen.
 */
const appCheck = (() => {
  if (import.meta.env.DEV && import.meta.env['VITE_APPCHECK_DEBUG'] === '1') {
    // Der Emulator kennt kein reCAPTCHA. Der ausgegebene Debug-Token muss
    // einmalig in der Firebase-Konsole hinterlegt werden.
    ;(globalThis as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean })
      .FIREBASE_APPCHECK_DEBUG_TOKEN = true
  }

  if (!RECAPTCHA_SITE_KEY) {
    console.warn(
      'App Check ist nicht eingerichtet: RECAPTCHA_SITE_KEY ist leer. ' +
        'Firebase Storage wird jeden Bildabruf mit 401 beantworten.',
    )
    return null
  }

  return initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  })
})()

export const appCheckEingerichtet = appCheck !== null

/**
 * Holt ein App-Check-Token und sagt, ob es klappt.
 *
 * Das Storage-SDK bildet **jede** 401 auf `storage/unauthenticated` ab — auch
 * die, die von App Check kommt. Die Meldung „bitte neu anmelden" schickt einen
 * Menschen dann auf die falsche Fährte, und Ab- und Anmelden ändert nichts.
 * Deshalb fragen wir im Fehlerfall hier nach, statt zu raten.
 */
export async function appCheckZustand(): Promise<
  { ok: true } | { ok: false; code: string; meldung: string }
> {
  if (!appCheck) {
    return {
      ok: false,
      code: 'appCheck/nicht-eingerichtet',
      meldung: 'App Check ist in dieser Auslieferung nicht eingerichtet.',
    }
  }
  try {
    await getToken(appCheck, false)
    return { ok: true }
  } catch (fehler) {
    const code =
      typeof fehler === 'object' && fehler !== null && 'code' in fehler
        ? String((fehler as { code: unknown }).code)
        : 'appCheck/unbekannt'
    return {
      ok: false,
      code,
      meldung: fehler instanceof Error ? fehler.message : String(fehler),
    }
  }
}

export const auth = getAuth(firebaseApp)

/**
 * Nur beim Entwickeln: gegen den Auth-Emulator statt gegen die echten Konten.
 * `import.meta.env.DEV` ist im gebauten Stand fest `false` — der Zweig fällt
 * beim Bauen heraus und kann in der Auslieferung nicht greifen.
 */
if (import.meta.env.DEV && import.meta.env['VITE_EMULATOR'] === '1') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true })
}

/** Bilder holt der Browser direkt aus Storage, nicht über das Backend. */
export const storage = getStorage(firebaseApp)

// Die Sitzung bleibt über das Schließen des Fensters hinaus bestehen (§5.2).
export const persistenzBereit = setPersistence(auth, browserLocalPersistence)
